/**
 * Audio Metadata Cache Service
 * Caches audio URLs and metadata to avoid repeated DB lookups
 */

interface AudioMetadata {
  audioUrl: string | null;
  duration: number | null;
  timestamp: number;
  courseId: string;
  moduleId: string;
}

class AudioMetadataCacheService {
  private cache = new Map<string, AudioMetadata>();
  private readonly CACHE_TTL = 1000 * 60 * 60 * 24 * 7; // PHASE 3: 7 days for maximum cache persistence
  private readonly STORAGE_KEY = 'audio-metadata-cache';
  private readonly MAX_CACHE_SIZE = 200;
  private saveTimeout: NodeJS.Timeout | null = null; // PHASE 3: Debounce saves
  private readonly SAVE_DEBOUNCE_MS = 1000; // Save at most once per second
  
  // Cache versioning for migration invalidation
  private readonly CACHE_VERSION = '2'; // Increment when audio data structure changes
  private readonly VERSION_KEY = 'audio-metadata-cache-version';
  // Invalidate cache entries created before the audio ID migration
  private readonly MIGRATION_DATE = new Date('2025-12-29T00:00:00Z').getTime();

  constructor() {
    this.loadFromStorage();
    // PHASE 3: Force immediate save on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        if (this.saveTimeout) {
          clearTimeout(this.saveTimeout);
          this.saveToStorageImmediate();
        }
      });
    }
  }

  /**
   * Get cached audio metadata
   */
  get(moduleId: string, courseId: string): AudioMetadata | null {
    const key = this.getCacheKey(moduleId, courseId);
    const cached = this.cache.get(key);

    if (!cached) return null;

    // Check if expired
    if (Date.now() - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      this.saveToStorage();
      return null;
    }

    return cached;
  }

  /**
   * Set audio metadata in cache
   */
  set(moduleId: string, courseId: string, audioUrl: string | null, duration: number | null): void {
    // FIX: Only cache entries that have actual audio URLs
    // Don't cache "no audio" entries as they may become stale when audio is generated later
    if (!audioUrl) {
      console.log(`⏭️ Skipping cache for ${moduleId} - no audio URL (will fetch fresh next time)`);
      return;
    }
    
    const key = this.getCacheKey(moduleId, courseId);
    
    // Enforce max cache size (LRU eviction)
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      audioUrl,
      duration,
      timestamp: Date.now(),
      courseId,
      moduleId
    });

    this.saveToStorage();
  }

  /**
   * Check if metadata exists in cache (even if null)
   */
  has(moduleId: string, courseId: string): boolean {
    const key = this.getCacheKey(moduleId, courseId);
    const cached = this.cache.get(key);
    
    if (!cached) return false;
    
    // Check if expired
    if (Date.now() - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      this.saveToStorage();
      return false;
    }
    
    return true;
  }

  /**
   * Batch set multiple audio metadata entries
   */
  batchSet(entries: Array<{ moduleId: string; courseId: string; audioUrl: string | null; duration: number | null }>): void {
    entries.forEach(entry => {
      this.set(entry.moduleId, entry.courseId, entry.audioUrl, entry.duration);
    });
  }

  /**
   * Preload audio metadata for multiple modules
   */
  async preloadCourse(courseId: string, audioData: Array<{ module_id: string; audio_url: string | null; duration_seconds: number | null }>): Promise<void> {
    audioData.forEach(audio => {
      this.set(audio.module_id, courseId, audio.audio_url, audio.duration_seconds);
    });
    console.log(`✅ Preloaded ${audioData.length} audio metadata entries for course ${courseId}`);
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    let cleared = 0;

    for (const [key, metadata] of this.cache.entries()) {
      if (now - metadata.timestamp > this.CACHE_TTL) {
        this.cache.delete(key);
        cleared++;
      }
    }

    if (cleared > 0) {
      this.saveToStorage();
      console.log(`🧹 Cleared ${cleared} expired audio metadata entries`);
    }
  }

  /**
   * Clear all cache (PHASE 1: User-accessible cache reset)
   */
  clear(): void {
    this.cache.clear();
    localStorage.removeItem(this.STORAGE_KEY);
    console.log('🗑️ Audio metadata cache cleared');
  }

  /**
   * Clear cache for specific module (PHASE 1: Targeted cache reset)
   */
  clearModule(moduleId: string, courseId: string): void {
    const key = this.getCacheKey(moduleId, courseId);
    this.cache.delete(key);
    this.saveToStorage();
    console.log(`🗑️ Cleared cache for module: ${key}`);
  }

  /**
   * Force refresh - clear cache and return null to trigger fresh fetch
   */
  forceRefresh(moduleId: string, courseId: string): void {
    this.clearModule(moduleId, courseId);
    console.log(`🔄 Forced refresh for module: ${moduleId}`);
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE,
      ttl: this.CACHE_TTL,
    };
  }

  private getCacheKey(moduleId: string, courseId: string): string {
    return `${courseId}:${moduleId}`;
  }

  /**
   * Load cache from localStorage
   */
  private loadFromStorage(): void {
    try {
      // Check cache version - invalidate if mismatch
      const storedVersion = localStorage.getItem(this.VERSION_KEY);
      if (storedVersion !== this.CACHE_VERSION) {
        console.log(`🔄 Cache version mismatch (${storedVersion} → ${this.CACHE_VERSION}) - clearing stale cache`);
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.setItem(this.VERSION_KEY, this.CACHE_VERSION);
        return;
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.cache = new Map(Object.entries(data));
        
        // Clear entries older than migration date (fixes stale "no audio" cache from before ID migration)
        this.clearOlderThan(this.MIGRATION_DATE);
        
        this.clearExpired(); // Clean on load
      }
    } catch (error) {
      console.warn('Failed to load audio metadata cache from storage:', error);
    }
  }

  /**
   * Clear entries older than a specific timestamp (for migration invalidation)
   */
  private clearOlderThan(timestamp: number): void {
    let cleared = 0;
    for (const [key, metadata] of this.cache.entries()) {
      if (metadata.timestamp < timestamp) {
        this.cache.delete(key);
        cleared++;
      }
    }
    if (cleared > 0) {
      this.saveToStorageImmediate();
      console.log(`🧹 Cleared ${cleared} cache entries older than migration date`);
    }
  }

  /**
   * Save cache to localStorage (PHASE 3: Debounced to reduce writes)
   */
  private saveToStorage(): void {
    // Debounce saves to reduce localStorage writes
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(() => {
      this.saveToStorageImmediate();
      this.saveTimeout = null;
    }, this.SAVE_DEBOUNCE_MS);
  }

  /**
   * Force immediate save to localStorage
   */
  private saveToStorageImmediate(): void {
    try {
      const data = Object.fromEntries(this.cache.entries());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save audio metadata cache to storage:', error);
      // If quota exceeded, clear old entries and retry
      this.clearExpired();
      try {
        const data = Object.fromEntries(this.cache.entries());
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      } catch (retryError) {
        console.error('Failed to save audio cache even after cleanup:', retryError);
      }
    }
  }
}

export const audioMetadataCache = new AudioMetadataCacheService();
